import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Box, MenuItem, Typography } from '@mui/material';
import clsx from 'clsx';
import { useResponsive } from 'src/hooks/useResponsive';
import { SuggestionsProvider } from 'src/hooks/useSuggestionsContext';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { MenuSearchButton } from 'components/menu/MenuSearchButton';
import { MenuSearch } from 'components/menu/MenuSearchDialog';
import { isDefined, isEmptyString, isNotNull } from 'lib/typeInference';
import { useLocalHistory } from 'lib/useLocalHistory';
import { setTimeslot } from 'store/basket/basketSlice';
import { ApplicationState } from 'store/store';
import { getDay, getTimeslotTimes } from '../helpers';
import { Timeslot } from '../model/Timeslot';
import { TimeslotPickerDialog } from './TimeslotPickerDialog';
import { useQuery } from 'src/hooks/useQuery';

const PREFIX = 'TimeslotPicker';

const classes = {
    confirmButton: `${PREFIX}confirmButton`,
    editText: `${PREFIX}editText`,
    textBold: `${PREFIX}textBold`,
    noPadding: `${PREFIX}noPadding`,
    textColor: `${PREFIX}textColor`,
    lineHeightReset: `${PREFIX}lineHeightReset`,
    collectionFontSize: `${PREFIX}collectionFontSize`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: theme.palette.titleTextColour,
    borderRadius: isDesktop ? theme.shape.borderRadius : 0,
    [`& .${classes.confirmButton}`]: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1)
    },

    [`& .${classes.editText}`]: {
        color: theme.palette.primary.contrastText
    },

    [`& .${classes.textBold}`]: {
        fontWeight: 700
    },

    [`& .${classes.noPadding}`]: {
        padding: theme.spacing(0)
    },

    [`& .${classes.textColor}`]: {
        color: theme.palette.background.default
    },

    [`& .${classes.lineHeightReset}`]: {
        lineHeight: '1'
    },

    [`& .${classes.collectionFontSize}`]: {
        fontSize: '1.125rem'
    }
}));

const StyledMenuCollectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1.125rem',
    lineHeight: '1',
    fontWeight: 700,
    color: theme.palette.background.default
}));

const StyledTitleText = styled(Typography)(({ theme }) => ({
    color: theme.palette.background.default,
    lineHeight: '1',
    // line clamped at 1
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: '1',
    WebkitBoxOrient: 'vertical',
    textWrap: 'wrap'
}));

export const TimeslotPicker: React.FC<React.PropsWithChildren> = () => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { push } = useLocalHistory();
    const { isDesktop } = useResponsive();

    const dispatch = useDispatch();
    const location = useLocation();
    const locationState = location.state as { isTimeslotPickerShown: boolean };
    const { isCurrentLocationLoading } = useSelector((state: ApplicationState) => state.locations);
    const { timeslots, settings } = useSelector((state: ApplicationState) => state.settings);
    const { timeSlot, prepTimePadding } = useSelector((state: ApplicationState) => state.basket);
    const { isLocationOpen, isLocationOrderEnabled } = useLocationHelpers();
    const currentSearch = useQuery('search');
    const [openMenuSearch, setOpenMenuSearch] = useState(() => {
        if (isNotNull(currentSearch) && isDefined(currentSearch) && !isEmptyString(currentSearch)) {
            return true;
        }
        return false;
    });

    const handleCloseMenuSearch = React.useCallback(() => {
        setOpenMenuSearch(false);
    }, []);
    const handleOpenMenuSearch = React.useCallback(() => {
        setOpenMenuSearch(true);
    }, []);

    const formattedTimeslots = React.useMemo(
        () =>
            timeslots
                ? timeslots.reduce<Record<string, Timeslot[]>>((acc, item) => {
                      const currentDay = getDay(item.start);
                      if (!Array.isArray(acc[currentDay])) {
                          acc[currentDay] = [item];
                      } else {
                          acc[currentDay].push(item);
                      }
                      return acc;
                  }, {})
                : {},
        [timeslots]
    );
    const noAsap = React.useMemo(
        () => (!settings?.asapOrderingEnabled || !isLocationOpen) && settings?.timeSlotsEnabled,
        [settings?.asapOrderingEnabled, settings?.timeSlotsEnabled, isLocationOpen]
    );
    const isEditEnabled = React.useMemo(
        () => !!timeslots && !!timeslots.length && !!settings?.timeSlotsEnabled,
        [timeslots, settings?.timeSlotsEnabled]
    );
    const description = React.useMemo(() => {
        if (!!timeSlot) {
            return getTimeslotTimes(timeSlot, t, true);
        }
        const timePadding = prepTimePadding ?? 0;
        return `${t('ORDER_STATUS_ORDER_READY_IN')} ${t('ORDER_STATUS_ORDER_READY_IN_MINS', {
            from: (settings?.preOrder?.readyPeriodStart || 5) + timePadding,
            to: (settings?.preOrder?.readyPeriodEnd || 20) + timePadding
        })}`;
    }, [
        t,
        timeSlot,
        settings?.preOrder?.readyPeriodStart,
        settings?.preOrder?.readyPeriodEnd,
        prepTimePadding
    ]);
    const [open, setOpen] = useState(
        () => !!(!isDefined(locationState?.isTimeslotPickerShown) && settings?.timeSlotsEnabled)
    );
    const handleEdit = React.useCallback(() => {
        if (!!timeslots && !!timeslots.length && !!settings?.timeSlotsEnabled && !!isLocationOrderEnabled) {
            setOpen(true);
        }
    }, [timeslots, settings?.timeSlotsEnabled, isLocationOrderEnabled]);
    const handleClose = React.useCallback(() => {
        setOpen(false);
        push(pathname, {}, '', { isTimeslotPickerShown: true });
    }, [pathname, push]);
    const handleSubmit = React.useCallback(
        (data?: { day: string; time: string }) => {
            dispatch(setTimeslot(data ? formattedTimeslots[data.day][Number(data.time)] : undefined));
            handleClose();
        },
        [handleClose, formattedTimeslots, dispatch]
    );
    React.useEffect(() => {
        if (noAsap && !timeSlot && timeslots && timeslots.length) {
            dispatch(setTimeslot(timeslots[0]));
        }
    }, [noAsap, timeSlot, timeslots, dispatch]);
    if (
        isCurrentLocationLoading ||
        !settings ||
        (settings.timeSlotsEnabled && (!timeslots || !timeslots.length))
    ) {
        return null;
    }
    return (
        <StyledBox isDesktop={isDesktop}>
            <MenuItem
                disabled={!isEditEnabled}
                onClick={handleEdit}
                sx={{ maxWidth: '100%', overflow: 'hidden' }}
            >
                <Box
                    display="flex"
                    flex={1}
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box display="flex" flexDirection="column" paddingTop={1} paddingBottom={1}>
                        <StyledMenuCollectionTitle>{t('MENU_COLLECTION')}</StyledMenuCollectionTitle>
                        <Box display="flex" alignItems="center" className={classes.lineHeightReset}>
                            <Box>
                                <StyledTitleText variant="caption">{description}</StyledTitleText>
                            </Box>
                            {settings.timeSlotsEnabled && (
                                <Box marginLeft={2.5}>
                                    <Typography
                                        className={clsx(
                                            classes.editText,
                                            classes.textBold,
                                            classes.lineHeightReset
                                        )}
                                        variant="caption"
                                    >
                                        {t('GENERAL_EDIT')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </MenuItem>
            <TimeslotPickerDialog
                open={open}
                noAsap={noAsap}
                onClose={handleClose}
                onSubmit={handleSubmit}
                options={formattedTimeslots}
                timeslot={timeSlot}
            />
            {!!openMenuSearch && (
                <SuggestionsProvider>
                    <MenuSearch open={openMenuSearch} onClose={handleCloseMenuSearch} />
                </SuggestionsProvider>
            )}
            <MenuSearchButton handleOpenMenuSearch={handleOpenMenuSearch} />
        </StyledBox>
    );
};
